/**
 * What a deal is worth, and what a column is worth.
 *
 * Kyu, 2 Sep: "how hard would it be to add small text line at the bottom for
 * the deal amount" — then "per deal, yes" and "yes just the dollar amount".
 *
 * ── Null is not zero, and this file exists mostly to keep it that way ──────
 *
 * `deals.amount` is nullable and NULL means NOBODY HAS PRICED THIS. A deal
 * worth nothing and a deal nobody has put a number on are different facts, and
 * every function here keeps them apart.
 *
 * That matters most in the column total. Adding 4 priced deals out of 9 and
 * printing the answer as though it were the column's worth is the same shape
 * of error as a conversion rate off the wrong denominator: the arithmetic is
 * right and the sentence is a lie. So `columnTotal` returns the count it could
 * not price alongside the money, and the header says so.
 */

/** Everything the board needs off a deal to talk about money. */
export interface Priced {
  amount: number | null;
}

/**
 * A dollar figure for the screen, or '' when there is nothing to show.
 *
 * Whole dollars carry no decimals — a board is scanned, and ".00" on every
 * card is noise repeated forty times. A value with cents keeps them, because
 * dropping them would silently show a number that is not the number stored.
 */
export function formatAmount(amount: number | null | undefined): string {
  if (amount === null || amount === undefined || !Number.isFinite(amount)) {
    return '';
  }
  const whole = Number.isInteger(amount);
  return amount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  });
}

export interface ColumnMoney {
  /** The sum of the deals that HAVE a number. */
  total: number;
  /** How many were added up. */
  priced: number;
  /** How many carry no amount, and so are missing from `total`. */
  unpriced: number;
}

/**
 * What a column adds up to, and how much of it is missing.
 *
 * `unpriced` is the whole point. A stage total that quietly leaves out five
 * deals reads as the stage's value and is not, and nobody looking at it can
 * tell. The header renders the gap rather than hiding it.
 */
export function columnTotal(deals: readonly Priced[]): ColumnMoney {
  let total = 0;
  let priced = 0;
  let unpriced = 0;
  for (const deal of deals) {
    if (deal.amount === null || deal.amount === undefined || !Number.isFinite(deal.amount)) {
      unpriced += 1;
      continue;
    }
    total += deal.amount;
    priced += 1;
  }
  return { total, priced, unpriced };
}

/**
 * The column header's money line, or '' when there is no money to talk about.
 *
 * An all-unpriced column says nothing rather than "$0" — a stage holding nine
 * deals nobody has priced is not a stage worth zero, and printing zero there
 * would be the exact confusion this module exists to prevent.
 */
export function columnMoneyLabel(money: ColumnMoney): string {
  if (money.priced === 0) {
    return '';
  }
  const sum = formatAmount(money.total);
  return money.unpriced === 0 ? sum : `${sum} · ${String(money.unpriced)} unpriced`;
}

/**
 * Reading what somebody typed into the amount field.
 *
 * Deliberately forgiving about how a person writes money — "$12,500", "12500",
 * " 12,500 " are the same number — because the alternative is a form that
 * rejects the way everybody actually types it.
 *
 * Returns `null` for an empty field, which is how a deal gets UNPRICED again;
 * `undefined` for something that is not a number at all, which the caller
 * turns into a message rather than a silent save of nothing.
 */
export function parseAmount(raw: string): number | null | undefined {
  const trimmed = raw.trim();
  if (trimmed === '') {
    return null;
  }
  // Strip only the things people type around a number, never digits or the
  // minus sign: a stripped minus would turn a typo into a plausible figure.
  const cleaned = trimmed.replace(/[$,\s]/g, '');
  if (!/^-?\d+(\.\d+)?$/.test(cleaned)) {
    return undefined;
  }
  const value = Number(cleaned);
  return Number.isFinite(value) ? value : undefined;
}

/** Why an amount was refused, in words somebody can act on. */
export function amountError(raw: string): string {
  return parseAmount(raw) === undefined
    ? `"${raw.trim()}" is not an amount. Type a number, like 12500 or $12,500.`
    : '';
}
